/**
 * Routes d’authentification et de session (DECISIONS §4.2, `docs/api-v1.md` §8.1–8.2).
 *
 * Le refresh token voyage **uniquement** dans un cookie `HttpOnly` : aucun de ces appels ne le
 * manipule. Le jeton d’accès renvoyé est stocké en mémoire par le store de session.
 */
import {
  loginResponseSchema,
  meResponseSchema,
  registerResponseSchema,
  type LoginInput,
  type LoginResponse,
  type MeResponse,
  type RegisterInput,
  type RegisterResponse,
} from '@elemora/shared/api';
import { API_PATHS } from '@elemora/shared/constants';

import { rafraichirAccessToken, requete, requeteSansContenu } from './http';
import { useSessionStore } from './session-store';

/** `POST /auth/login` : jeton d’accès + compte + progression. */
export async function connecter(entree: LoginInput): Promise<LoginResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.auth.login,
    schema: loginResponseSchema,
    corps: entree,
    authentifiee: false,
  });
}

/**
 * `POST /auth/register` : crée le compte.
 *
 * Le contrat ne renvoie **pas** de jeton d’accès à l’inscription (`{ user, progression }`) :
 * l’écran d’inscription enchaîne donc un `POST /auth/login` avec les mêmes identifiants.
 */
export async function inscrire(entree: RegisterInput): Promise<RegisterResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.auth.register,
    schema: registerResponseSchema,
    corps: entree,
    authentifiee: false,
  });
}

/** `GET /me` : compte et progression **d’autorité** (source de l’affichage des éclats). */
export async function recupererProfil(): Promise<MeResponse> {
  return requete({ methode: 'GET', chemin: API_PATHS.me.root, schema: meResponseSchema });
}

/**
 * Restaure une session après rechargement de page.
 *
 * Aucun jeton n’est persisté : on repart du cookie `HttpOnly` via `POST /auth/refresh`, puis on
 * relit `/me`. Renvoie `false` si le cookie est absent, expiré ou révoqué.
 */
export async function restaurerSession(): Promise<boolean> {
  const accessToken = await rafraichirAccessToken();
  if (accessToken === null) return false;
  try {
    const profil = await recupererProfil();
    useSessionStore
      .getState()
      .definirSession({ user: profil.user, progression: profil.progression, accessToken });
    return true;
  } catch {
    useSessionStore.getState().deconnecter();
    return false;
  }
}

/**
 * `POST /auth/logout` : révoque la session serveur puis purge l’état local.
 *
 * La purge a lieu même si l’appel échoue : le joueur ne doit jamais rester « connecté » dans
 * l’interface alors que le serveur a déjà oublié la session.
 */
export async function fermerSession(): Promise<void> {
  try {
    await requeteSansContenu({
      methode: 'POST',
      chemin: API_PATHS.auth.logout,
      authentifiee: false,
    });
  } finally {
    useSessionStore.getState().deconnecter();
  }
}
